// ./src/pages/private/EventTicketsPage/TicketCouponGrantsDrawer.tsx
//
// Which vouchers a ticket type grants ("this ticket comes with 2 drink coupons").
// Opened from the ticket card. Deliberately the SAME editor and the same
// save-cancel shape as the event's RSVP coupon rules (CouponGrantRulesDrawer) —
// it's the same question, so it should look and behave the same. Only the last
// column's wording differs: per ticket / per order here vs per person / per group
// there.
//
// Unlike the RSVP rules (a settings blob), each row here is a ticket_type_coupon
// RECORD, so Save diffs the rows against what was loaded and issues the
// create/update/remove mutations. couponTypeId is create-only server-side, so
// re-pointing a row at another voucher is a remove + create.
//
// ⚠ #283 — these are issued for real: `CouponCore.applyTicketGrantsForOrder` runs when an order completes (online, at the
// box office, and on an approval once settled), reading these rules at that moment: an order still awaiting approval gets
// today's set, and vouchers already issued are not touched. The banner says so.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stack, Alert } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { TicketType, CouponType, CouponTypeService, COUPON_GRANT_PER } from "@/utils/api";
import { useTicketTypeCoupons, useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, AppIcon, PremiumFeatureGate, FeatureGateContent } from "@/components/generic";
import { formatErrors, getIndexedFormErrors, indexedFieldName, refusalReason } from "@/utils";
import { FEATURE_CODES } from "@/config";
import { CouponGrantRulesEditor, GrantRuleRow } from "@/components/features/Coupons";

export interface TicketCouponGrantsDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticket: TicketType | null;
  /** Reload the tickets list (to refresh the grant-count badge) after changes. */
  onChanged: () => void;
  /**
   * The account's plan does not include `promotions`.
   *
   * ⚠ THE DRAWER STILL OPENS — it explains what coupon grants are and offers Subscribe, instead
   * of the row action bouncing the host to the billing page (owner, 2026-09-12). Computed ONCE by
   * the page from the single resolver and passed down; this component must not ask again.
   *
   * ⚠ REQUIRED, AND IT HAS NO DEFAULT ON PURPOSE (`no-defaults-on-gating-flags`). `locked = false`
   * means a call site that forgets the prop silently gets the fully unlocked PROMOTIONS editor and
   * `tsc` says nothing — the gate would be absent, not merely wrong. Required moves that to compile
   * time: a new caller cannot mount this drawer without stating the plan's answer.
   */
  locked: boolean;
}

/** What each loaded row looked like on open — the baseline Save diffs against. */
interface Baseline {
  couponTypeId: string;
  quantity: number;
  grantPer: string;
}

/**
 * A stored 'guest' grant has no control to sit in (the editor offers ticket/order),
 * and would render as nothing selected. One ticket admits exactly one guest, so
 * showing it as per-ticket is what it already means — not a silent rewrite. It only
 * persists if the host edits that row.
 */
const displayGrantPer = (grantPer?: string | null): string =>
  grantPer === COUPON_GRANT_PER.ORDER ? COUPON_GRANT_PER.ORDER : COUPON_GRANT_PER.TICKET;

/**
 * The only names the editor draws on a row's controls. ⚠ Anything else (ticketTypeId,
 * status, an id) must stay GENERIC: re-keyed to `grants[i].<name>` it would land in a map
 * nothing renders and the refusal would vanish — the swallow, one level down.
 */
const ROW_FIELDS = new Set(["couponTypeId", "quantity", "grantPer"]);

export const TicketCouponGrantsDrawer: React.FC<TicketCouponGrantsDrawerProps> = ({
  opened,
  onClose,
  ticket,
  onChanged,
  locked,
}) => {
  const { t } = useTranslation(["events", "coupons", "common"]);
  const ticketTypeId = ticket?.id ?? null;

  // ⚠ A locked drawer loads NOTHING — neither the grants nor the voucher catalogue below. There
  // is no editor to seed, and the server would refuse every write anyway.
  const { grants, loadedFor, addGrant, updateGrant, removeGrant } = useTicketTypeCoupons(
    opened && !locked ? ticketTypeId : null
  );

  const [vouchers, setVouchers] = useState<CouponType[]>([]);
  /**
   * ⚠ Whether the FIRST read has COME BACK — not the same as "not loading". The read starts in an
   * effect, which runs after the first paint, so a branch on `loading && rows.length === 0` shows
   * the empty state for one frame every time this opens. ▶ npm run check-list-states
   */
  const [vouchersLoaded, setVouchersLoaded] = useState(false);
  const [rows, setRows] = useState<GrantRuleRow[]>([]);
  const [baseline, setBaseline] = useState<Map<string, Baseline>>(new Map());
  const [saving, setSaving] = useState(false);
  // The OpResult of THIS drawer's own save, re-keyed so each refusal knows its row.
  const [submitResult, setSubmitResult] = useState<OpResult | null>(null);

  // The account's reusable vouchers — what a grant can point at.
  useEffect(() => {
    if (!opened || locked) {
      return;
    }

    let cancelled = false;
    setSubmitResult(null);
    setVouchersLoaded(false);

    // contextEventId (the ticket's event) scopes the voucher list to that event's account so a
    // cross-account crew member (co_host with tickets.manage) sees the account's vouchers.
    CouponTypeService.list({ reusableOnly: true, status: [STATUSES.ACTIVE], contextEventId: ticket?.eventId ?? null }).then((result) => {
      if (cancelled) {
        return;
      }

      // ⚠ Before the branch — a read that FAILED has still come back.
      setVouchersLoaded(true);

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        setVouchers(result.data as CouponType[]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [opened, locked, ticket?.eventId]);

  // Seed the editable rows (and the Save baseline) from the loaded records — once
  // per open, and only once THIS ticket's rows have actually landed. Keying this
  // on a loaded flag instead seeds from the still-empty list and the rows never
  // appear; keying it on `grants` alone would clobber the host's edits on every
  // local list change.
  const seededRef = useRef(false);
  // Esc / a click outside / Cancel ask before discarding changed rows (#123). Seeded where the rows are — when THIS
  // ticket's grants land, not on open — so a drawer still loading has nothing to lose.
  const { isDirty, seedFrom, clear } = useFormSnapshot(rows);

  useEffect(() => {
    if (!opened) {
      seededRef.current = false;
      clear();

      return;
    }
  }, [opened, clear]);

  useEffect(() => {
    if (!opened || !ticketTypeId || loadedFor !== ticketTypeId || seededRef.current) {
      return;
    }

    seededRef.current = true;

    const seeded = grants.map((g) => ({
      key: g.id as string,
      couponTypeId: g.couponTypeId ?? "",
      quantity: g.quantity ?? 1,
      grantPer: displayGrantPer(g.grantPer),
    }));
    setRows(seeded);
    seedFrom(seeded);
    // The baseline holds the DISPLAYED value, so a legacy 'guest' row the host
    // never touches isn't rewritten by a save it wasn't part of.
    setBaseline(
      new Map(
        grants.map((g) => [
          g.id as string,
          { couponTypeId: g.couponTypeId ?? "", quantity: g.quantity ?? 1, grantPer: displayGrantPer(g.grantPer) },
        ])
      )
    );
  }, [opened, ticketTypeId, loadedFor, grants, seedFrom]);

  const typeOptions = useMemo(
    () =>
      vouchers
        .filter((v) => v.id)
        .map((v) => ({ value: v.id as string, label: v.name || t("coupons:untitledType", { defaultValue: "Voucher" }) })),
    [vouchers, t]
  );

  // Ticket = one lot per admission, Order = one lot per transaction — the same
  // per-unit / per-lot pair the RSVP rules offer as Person / Group. (The column
  // also accepts 'guest', but a ticket admits exactly one guest, so it would count
  // identically to Ticket — offering both would be a distinction without a
  // difference.)
  const perOptions = useMemo(
    () => [
      { value: COUPON_GRANT_PER.TICKET, label: t("events:ticketGrants.perTicket", { defaultValue: "Ticket" }) },
      { value: COUPON_GRANT_PER.ORDER, label: t("events:ticketGrants.perOrder", { defaultValue: "Order" }) },
    ],
    [t]
  );

  const handleSubmit = useCallback(async () => {
    if (!ticketTypeId) {
      return;
    }

    const clean = rows.filter((r) => r.couponTypeId && r.quantity >= 1);
    const keptIds = new Set(clean.filter((r) => baseline.has(r.key)).map((r) => r.key));

    setSaving(true);
    setSubmitResult(null);

    // Each save is a batch of single-record calls, so a bare list of results would lose
    // WHICH row the server refused. Keep the row's key with its result.
    const failures: { rowKey: string | null; result: OpResult }[] = [];
    const record = (rowKey: string | null, result: OpResult): void => {
      if (!result.didSucceed()) {
        failures.push({ rowKey, result });
      }
    };

    // Rows the host deleted (or emptied) — gone from `clean` but in the baseline. There is
    // no row left on screen to point at, so these stay generic.
    for (const id of baseline.keys()) {
      if (!keptIds.has(id)) {
        record(null, await removeGrant(id));
      }
    }

    for (const row of clean) {
      const base = baseline.get(row.key);

      if (!base) {
        record(row.key, await addGrant({ ticketTypeId, couponTypeId: row.couponTypeId, quantity: row.quantity, grantPer: row.grantPer }));

        continue;
      }

      // The voucher is create-only server-side → swap = remove + create.
      if (base.couponTypeId !== row.couponTypeId) {
        record(row.key, await removeGrant(row.key));
        record(row.key, await addGrant({ ticketTypeId, couponTypeId: row.couponTypeId, quantity: row.quantity, grantPer: row.grantPer }));

        continue;
      }

      if (base.quantity !== row.quantity || base.grantPer !== row.grantPer) {
        record(row.key, await updateGrant(row.key, { quantity: row.quantity, grantPer: row.grantPer }));
      }
    }

    setSaving(false);

    if (failures.length > 0) {
      // Re-key every refusal to the row it came from (`grants[<index>].<field>`), so the
      // editor can draw it on that row's control. Indices are into `rows` — what the
      // editor actually renders. Some changes may have landed before the failure, so the
      // drawer stays OPEN and shows what stuck rather than closing on a half-applied save.
      const failResult = new OpResult({ code: OP_RESULT_CODES.VALIDATION_FAILED });

      for (const { rowKey, result } of failures) {
        const index = rowKey ? rows.findIndex((r) => r.key === rowKey) : -1;
        const items = (result.errors as { name?: string; errors?: string[] }[] | undefined) ?? [];
        let added = 0;

        for (const item of items) {
          const name = item?.name || "";

          for (const message of item?.errors ?? []) {
            added += 1;

            if (index >= 0 && ROW_FIELDS.has(name)) {
              failResult.addError(indexedFieldName("grants", index, name), message);
            } else {
              failResult.addError("", name ? `${name}: ${message}` : message);
            }
          }
        }

        // A transport/permission failure can carry no named errors at all — never let it
        // pass in silence just because the shape was unexpected.
        if (added === 0) {
          failResult.addError(
            "",
            refusalReason(result) || t("events:ticketGrants.saveFailed", { defaultValue: "Could not save the grants." })
          );
        }
      }

      setSubmitResult(failResult);

      return;
    }

    onChanged();
    onClose();
  }, [ticketTypeId, rows, baseline, addGrant, updateGrant, removeGrant, onChanged, onClose, t]);

  // Row-scoped refusals go on that row's controls; everything else goes to the drawer's
  // footer alert, right next to Save.
  const { byIndex: rowErrors, genericErrors } = useMemo(() => getIndexedFormErrors(submitResult, "grants"), [submitResult]);
  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);
  // A row error can be scrolled out of view, so the footer gets a pointer to it too.
  const hasFieldErrors = useMemo(
    () => Object.values(rowErrors).some((row) => Object.values(row).some(Boolean)),
    [rowErrors]
  );

  // ⚠ `canConfirm: !locked` too — Esc must still close, but ⌘S on a locked drawer would fire a
  // save the server refuses. A keyboard path around a removed button is still a path.
  useModalShortcuts({ onConfirm: handleSubmit, canConfirm: !saving && !locked, enabled: opened });

  // Skeletons until THIS ticket's grants are in hand.
  const loadingGrants = !!ticketTypeId && loadedFor !== ticketTypeId;
  const noVouchers = vouchersLoaded && vouchers.length === 0;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("events:ticketGrants.title", {
        defaultValue: "Voucher grants — {{ticket}}",
        ticket: ticket?.name || t("events:tickets.untitled", { defaultValue: "Ticket" }),
      })}
      position="right"
      size="lg"
      loading={saving}
      // ⚠ A locked drawer has NO Save. Offering one over a body that is an upgrade pitch would
      // be a button that can only fail — the server refuses `promotions` writes regardless.
      actionType={locked ? "close" : "save-cancel"}
      onConfirm={locked ? undefined : handleSubmit}
      confirmShortcut={locked ? undefined : SHORTCUT_HINTS.save}
      cancelLabel={locked ? t("common:buttons.close", { defaultValue: "Close" }) : undefined}
      errors={formattedGenericError}
      hasFieldErrors={hasFieldErrors}
      // A locked drawer is an upgrade pitch with no editor — nothing to lose.
      isDirty={!locked && isDirty}
    >
      {locked ? (
        <PremiumFeatureGate
          featureCode={FEATURE_CODES.PROMOTIONS}
          eventId={ticket?.eventId ?? null}
          variant="panel"
          featureDescription={<FeatureGateContent gateKey="ticketCouponGrants" featureCode={FEATURE_CODES.PROMOTIONS} />}
        >
          {null}
        </PremiumFeatureGate>
      ) : (
      <Stack gap="md">
        <Alert color="blue" icon={<AppIcon iconName="InfoCircle" size={16} />}>
          {t("events:ticketGrants.whenIssued", {
            defaultValue:
              "Every order gets these vouchers as soon as it is complete: online, at the door, and once approved on an event that reviews buyers. A change here applies to orders completed after it; vouchers already issued stay as they are.",
          })}
        </Alert>

        {noVouchers && (
          <Alert color="yellow" icon={<AppIcon iconName="InfoCircle" size={16} />}>
            {t("events:ticketGrants.noVouchers", {
              defaultValue: "No reusable vouchers yet — create one under Library → Vouchers first.",
            })}
          </Alert>
        )}

        <CouponGrantRulesEditor
          rows={rows}
          onChange={setRows}
          typeOptions={typeOptions}
          perOptions={perOptions}
          defaultGrantPer={COUPON_GRANT_PER.TICKET}
          loading={loadingGrants}
          skeletonRows={2}
          disabled={saving}
          rowErrors={rowErrors}
          emptyText={t("events:ticketGrants.empty", {
            defaultValue: "No vouchers with this ticket yet — add one to bundle it with every ticket sold.",
          })}
          addLabel={t("events:ticketGrants.add", { defaultValue: "Add voucher" })}
          hint={t("events:ticketGrants.hint", {
            defaultValue:
              "Ticket = one lot per ticket bought (3 tickets → 3 lots). Order = one lot per order, however many tickets it holds.",
          })}
        />
      </Stack>
      )}
    </AppDrawer>
  );
};
